import { PlusCircleIcon, TrashIcon } from "@heroicons/react/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { HttpError, useOne, useTranslation } from "@refinedev/core";
import Button from "components/button";
import DateTimePicker from "components/datetimePicker";
import Input from "components/input";
import { Label } from "components/label";
import { IModalProps } from "components/modal";
import { RightSideModal } from "components/rightSideModal";
import Select from "components/select";
import { endpointService } from "components/shared/services/endpointService";
import { SwitchButton } from "components/switchButton";
import { TextEditor } from "components/textEditor";
import {
  IMultilingualContent,
  INoPhaseTicketForm,
  ISimpleEventDetail,
  ISubTicketItem,
  PriceType,
  ProductType,
  SupportedCurrency,
  SupportedLanguage,
  UserType,
} from "interfaces";
import { get } from "lodash";
import moment from "moment";
import { useEffect } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { languageSchema } from "components/shared/event/schema";
import { LANGUAGES, PRICE_TYPES, SELLING_TYPE } from "utills/constants";
import i18n from "utills/i18n";
import { z } from "zod";
import { canAccessByType } from "utills/helpers";

interface NoPhaseTicketFormProps extends IModalProps {
  eventDetailData: ISimpleEventDetail;
  onSubmit: (data: INoPhaseTicketForm) => void;
  productType: ProductType;
  defaultValues: ISubTicketItem | null;
}

interface IAttendantTemplateField {
  id: string;
  template_id: string;
  name: IMultilingualContent;
}

const NoPhaseTicketSchema = (languages: SupportedLanguage[], currency: SupportedCurrency) => {
  const ticketSchema = (priceType: PriceType) => {
    return z.object({
      price_type: z.literal(priceType),
      category: languageSchema(languages),
      type_name: languageSchema(languages),
      short_description: z.object({
        en: z.string().optional(),
        vi: z.string().optional(),
      }),
      selling_min_qty: z
        .number({
          invalid_type_error: i18n.t("validation.errorMessage.mustBeANumber"),
          required_error: i18n.t("validation.errorMessage.thisFieldIsRequired"),
        })
        .min(1, {
          message: i18n.t("validation.errorMessage.minNum", { min: 1 }),
        })
        .nonnegative({ message: i18n.t("validation.errorMessage.nonNegativeNum") }),
      selling_max_qty: z
        .number({
          invalid_type_error: i18n.t("validation.errorMessage.mustBeANumber"),
          required_error: i18n.t("validation.errorMessage.thisFieldIsRequired"),
        })
        .min(1, {
          message: i18n.t("validation.errorMessage.minNum", { min: 1 }),
        })
        .nonnegative({ message: i18n.t("validation.errorMessage.nonNegativeNum") }),
      selling_type: z
        .string()
        .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
      original_price:
        priceType === PriceType.PAID
          ? z
              .number({
                invalid_type_error: i18n.t(
                  "validation.errorMessage.mustBeANumber"
                ),
                required_error: i18n.t(
                  "validation.errorMessage.thisFieldIsRequired"
                ),
              })
              .min(currency === SupportedCurrency.VND ? 10000 : 0, {
                message: i18n.t("validation.errorMessage.minNum", {
                  min: currency === SupportedCurrency.VND ? 10000 : 0,
                }),
              })
              .nonnegative({
                message: i18n.t("validation.errorMessage.nonNegativeNum"),
              })
          : z
              .number({
                invalid_type_error: i18n.t(
                  "validation.errorMessage.mustBeANumber"
                ),
                required_error: i18n.t(
                  "validation.errorMessage.thisFieldIsRequired"
                ),
              })
              .nonnegative({
                message: i18n.t("validation.errorMessage.nonNegativeNum"),
              }),
      price:
        priceType === PriceType.PAID
          ? z
              .number({
                invalid_type_error: i18n.t(
                  "validation.errorMessage.mustBeANumber"
                ),
                required_error: i18n.t(
                  "validation.errorMessage.thisFieldIsRequired"
                ),
              })
              .min(currency === SupportedCurrency.VND ? 10000 : 0, {
                message: i18n.t("validation.errorMessage.minNum", {
                  min: currency === SupportedCurrency.VND ? 10000 : 0,
                }),
              })
              .nonnegative({
                message: i18n.t("validation.errorMessage.nonNegativeNum"),
              })
          : z
              .number({
                invalid_type_error: i18n.t(
                  "validation.errorMessage.mustBeANumber"
                ),
                required_error: i18n.t(
                  "validation.errorMessage.thisFieldIsRequired"
                ),
              })
              .nonnegative({
                message: i18n.t("validation.errorMessage.nonNegativeNum"),
              }),
      sell_from: z
        .string()
        .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
      sell_to: z
        .string()
        .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
      attendant_template: z
        .array(
          z.object({
            template_id: z
              .string()
              .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
            name: z.object({
              en: z.string().optional(),
              vi: z.string().optional(),
            }),
          })
        )
        .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
      hide_in_ui: z.boolean(),
      is_package: z.boolean(),
      is_accept_edit_participant_info: z.boolean(),
      is_accept_upgrade_ticket: z.boolean(),
      is_accept_transfer_ticket: z.boolean(),
      is_soldout: z.boolean(),
      is_active: z.boolean(),
      ticket_phase: z.string(),
      total_for_sell: z
        .union([
          z
            .number()
            .int()
            .min(0, i18n.t("validation.errorMessage.minNum", { min: 0 }))
            .nullable(),
          z.nan(),
        ])
        .optional(),
      is_use_private_flow: z.boolean(),
      is_use_actiup_private_flow: z.boolean(),
    });
  };

  return z
    .discriminatedUnion("price_type", [
      ticketSchema(PriceType.FREE),
      ticketSchema(PriceType.PAID),
    ])
    .refine(
      (data) => moment(data.sell_from).isSameOrBefore(moment(data.sell_to)),
      {
        message: i18n.t("validation.errorMessage.endDateCannotBeEarlier"),
        path: ["sell_to"],
      }
    )
    .refine((data) => data.selling_min_qty <= data.selling_max_qty, {
      message: i18n.t("validation.errorMessage.cannotBeSmaller", {
        field: i18n.t(
          "eventSetup.phaseAndTicket.modal.ticket.minSelectionQuantity"
        ),
      }),
      path: ["selling_max_qty"],
    })
    .refine((data) => data.original_price >= data.price, {
      message: i18n.t("validation.errorMessage.cannotBeSmaller", {
        field: i18n.t("eventSetup.phaseAndTicket.modal.ticket.sellPrice"),
      }),
      path: ["original_price"],
    })
    .superRefine(
      (
        { selling_min_qty, selling_max_qty, is_package, attendant_template },
        ctx
      ) => {
        if (selling_max_qty < selling_min_qty) {
          ctx.addIssue({
            path: ["selling_max_qty"],
            code: "custom",
            message: i18n.t("validation.errorMessage.cannotBeSmaller", {
              field: i18n.t(
                "eventSetup.phaseAndTicket.modal.ticket.minSelectionQuantity"
              ),
            }),
          });
        }
        if (is_package) {
          if (attendant_template.length < 2) {
            ctx.addIssue({
              path: ["attendant_template"],
              code: "custom",
              message: i18n.t(
                "validation.errorMessage.minimumForPackageTicket",
                {
                  min: 2,
                }
              ),
            });
          }
        }
      }
    );
};

const DEFAULLT_TEMPLATE = [
  {
    template_id: "",
    name: {
      en: "",
      vi: "",
    },
  },
];

const getDefaultTicketValue = (defaultValues: ISubTicketItem | null) => {
  return {
    category: defaultValues?.category ?? {
      en: "",
      vi: "",
    },
    selling_min_qty: defaultValues?.selling_min_qty ?? 1,
    selling_max_qty: defaultValues?.selling_max_qty ?? 10,
    selling_type: defaultValues?.selling_type ?? "multiple",
    is_package: defaultValues?.is_package ?? false,
    attendant_template: defaultValues?.attendant_template ?? DEFAULLT_TEMPLATE,
    type_name: defaultValues?.type_name ?? {
      en: "",
      vi: "",
    },
    short_description: defaultValues?.short_description ?? {
      en: "",
      vi: "",
    },
    price_type: defaultValues?.price_type ?? PriceType.PAID,
    original_price: defaultValues?.original_price ?? 0,
    price: defaultValues?.price ?? 0,
    sell_from: defaultValues
      ? defaultValues?.sell_from
        ? moment(defaultValues.sell_from).format("YYYY-MM-DD HH:mm:ss")
        : ""
      : moment().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    sell_to: defaultValues
      ? defaultValues?.sell_to
        ? moment(defaultValues.sell_to).format("YYYY-MM-DD HH:mm:ss")
        : ""
      : moment().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
    is_accept_edit_participant_info:
      defaultValues?.is_accept_edit_participant_info ?? true,
    is_accept_upgrade_ticket: defaultValues?.is_accept_upgrade_ticket ?? false,
    is_accept_transfer_ticket:
      defaultValues?.is_accept_transfer_ticket ?? false,
    is_soldout: defaultValues?.is_soldout ?? false,
    is_active: defaultValues?.is_active ?? true,
    ticket_phase: defaultValues?.ticket_phase ?? "",
    hide_in_ui: defaultValues?.hide_in_ui ?? false,
    total_for_sell: defaultValues?.total_for_sell ?? null,
    is_use_private_flow: defaultValues?.is_use_private_flow ?? false,
    is_use_actiup_private_flow: defaultValues?.is_use_actiup_private_flow ?? false,
  };
};
export const NoPhaseTicketForm = ({
  open,
  onClose,
  eventDetailData,
  onSubmit,
  productType,
  defaultValues,
  title,
}: NoPhaseTicketFormProps) => {
  const { translate } = useTranslation();
  const isAdmin = canAccessByType(UserType.ADMIN);
  const canUseActiupPrivateFlow = isAdmin && productType === ProductType.TICKET;
  const { data } = useOne({
    resource: endpointService.getEventEndpoint(
      "getAttendantTemplateByEventId",
      {
        eventId: eventDetailData.id ?? "",
      }
    ),
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!eventDetailData.id,
    } as any,
  });

  const templatesDataArr = (data?.data ?? []).map((template: any) => ({
    label: get(template, "attendant_template_name", ""),
    value: get(template, "id", ""),
  }));

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm<INoPhaseTicketForm, HttpError, INoPhaseTicketForm>({
    defaultValues: getDefaultTicketValue(defaultValues),
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        NoPhaseTicketSchema(eventDetailData.languages, eventDetailData.currency)
      );
      return createResolver(values, context, options);
    },
  });

  useEffect(() => {
    if (open) {
      reset(getDefaultTicketValue(defaultValues));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "attendant_template",
  });

  const _submit = (data: INoPhaseTicketForm) => {
    const ticket = canUseActiupPrivateFlow
      ? data
      : (() => {
          const { is_use_actiup_private_flow, ...submitData } = data;
          return submitData;
        })();
    onSubmit({
      ...ticket,
      product_type: productType,
    });
  };

  console.log(errors);

  return (
    <RightSideModal
      title={
        title ??
        `${translate("add")} ${translate("eventSetup.phaseAndTicket.ticket")}`
      }
      open={open}
      onClose={onClose}
      footer={
        <div className="w-full flex flex-row gap-x-2 items-center">
          <Button
            className="w-full bg-white border-none text-brand-500 ring-1 ring-inset ring-brand-500 hover:bg-gray-50 active:bg-gray-100"
            onClick={onClose}
          >
            {translate("button.cancel")}
          </Button>
          <Button className="w-full uppercase" onClick={handleSubmit(_submit)}>
            {translate("button.save")}
          </Button>
        </div>
      }
    >
      <div className="p-4 sm:px-6">
        <div className="grid sm:grid-cols-6 gap-y-6 gap-x-4">
          <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
            <p className="text-sm">
              {translate("eventSetup.phaseAndTicket.modal.ticket.ticketInfo")}
            </p>
            {defaultValues?.id ? (
              <p className="text-sm mt-1">
                {translate("eventSetup.phaseAndTicket.modal.ticket.productId")}:{" "}
                {defaultValues?.id}
              </p>
            ) : (
              <></>
            )}
          </div>
          <div className="col-span-full">
            <Label
              required
              id={`category_${eventDetailData.languages[0] ?? "en"}`}
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.category"
              )}
            />
            <div className="flex flex-col gap-y-4">
              {eventDetailData.languages.map((lang) => (
                <div key={lang}>
                  <div className="relative" key={lang}>
                    <Input
                      id={`category_${lang}`}
                      type="text"
                      className="pr-11"
                      placeholder={`${translate(
                        "eventSetup.phaseAndTicket.modal.ticket.category"
                      )} (${lang})`}
                      {...register(`category.${lang}`)}
                      error={get(errors, `category.${lang}.message`, "")}
                    />
                    <img
                      src={`/images/flags/circle-${lang}.png`}
                      alt="Language Icon"
                      className="absolute top-1/2 right-3 -translate-y-1/2"
                    />
                  </div>
                  {get(errors, `category.${lang}`, "") ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(errors, `category.${lang}.message`, "")}
                      </span>
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-full">
            <Label
              required
              id={`type_name_${eventDetailData.languages[0] ?? "en"}`}
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.ticketNameDisplayed"
              )}
            />
            <div className="flex flex-col gap-y-4">
              {eventDetailData.languages.map((lang) => (
                <div key={lang}>
                  <div className="relative" key={lang}>
                    <Input
                      id={`type_name_${lang}`}
                      type="text"
                      className="pr-11"
                      placeholder={`${translate(
                        "eventSetup.phaseAndTicket.modal.ticket.ticketNameDisplayed"
                      )} (${lang})`}
                      {...register(`type_name.${lang}`)}
                      error={get(errors, `type_name.${lang}.message`, "")}
                    />
                    <img
                      src={`/images/flags/circle-${lang}.png`}
                      alt="Language Icon"
                      className="absolute top-1/2 right-3 -translate-y-1/2"
                    />
                  </div>
                  {get(errors, `type_name.${lang}`, "") ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(errors, `type_name.${lang}.message`, "")}
                      </span>
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-full">
            <Label
              id={`short_description_${eventDetailData.languages[0] ?? "en"}`}
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.noteBelowTicketName"
              )}
            />
            <div className="flex flex-col gap-y-4">
              {eventDetailData.languages.map((lang: SupportedLanguage) => (
                <div key={lang}>
                  <div
                    className={`w-fit flex flex-row items-center gap-x-1 p-2 -mb-1 rounded-tl-md rounded-tr-md border-magnolia-600 border-solid border-[1px] bg-magnolia-400 font-medium text-sm`}
                  >
                    <img
                      src={`/images/flags/circle-${lang}.png`}
                      alt="Language Icon"
                      className="w-4 shrink-0"
                    />
                    <p>
                      {
                        LANGUAGES.find((language) => language.value === lang)
                          ?.label
                      }
                    </p>
                  </div>

                  <Controller
                    control={control}
                    name={`short_description.${lang}`}
                    render={({ field: { onChange, value } }) => (
                      <TextEditor
                        key={`short_description.${lang}`}
                        value={value ?? ""}
                        placeholder={`${translate(
                          "eventSetup.phaseAndTicket.modal.ticket.noteBelowTicketName"
                        )} (${lang})`}
                        onChange={onChange}
                      />
                    )}
                  />

                  {get(errors, `short_description.${lang}`, "") ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(errors, `short_description.${lang}.message`, "")}
                      </span>
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
          <div className="sm:col-span-3">
            <Label
              id="selling_min_qty"
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.minSelectionQuantity"
              )}
              required
            />
            <Input
              id="selling_min_qty"
              type="number"
              min={0}
              placeholder={translate(
                "eventSetup.phaseAndTicket.modal.ticket.minSelectionQuantity"
              )}
              {...register(`selling_min_qty`, {
                valueAsNumber: true,
              })}
              error={get(errors, `selling_min_qty.message`, "")}
            />
            {get(errors, `selling_min_qty`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `selling_min_qty.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label
              id="selling_max_qty"
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.maxSelectionQuantity"
              )}
              required
            />
            <Input
              id="selling_max_qty"
              type="number"
              min={0}
              placeholder={translate(
                "eventSetup.phaseAndTicket.modal.ticket.maxSelectionQuantity"
              )}
              {...register(`selling_max_qty`, {
                valueAsNumber: true,
              })}
              error={get(errors, `selling_max_qty.message`, "")}
            />
            {get(errors, `selling_max_qty`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `selling_max_qty.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label
              id="selling_type"
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.sellingType"
              )}
              required
            />
            <Controller
              control={control}
              name="selling_type"
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <Select
                    key="selling_type"
                    inputRef={ref}
                    options={SELLING_TYPE}
                    id="selling_type"
                    placeholder={translate(
                      "eventSetup.phaseAndTicket.modal.ticket.sellingType"
                    )}
                    defaultValue={
                      SELLING_TYPE.find(
                        (selected: { label: string; value: string }) =>
                          selected.value === value
                      ) ?? ""
                    }
                    value={SELLING_TYPE.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === value
                    )}
                    onChange={(selected: { label: string; value: string }) => {
                      onChange(selected?.value);
                    }}
                    error={get(errors, `selling_type`, "")}
                  />
                );
              }}
            />
            {get(errors, `selling_type`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `selling_type.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          {productType === ProductType.TICKET ? ( // hidden for group tickets
            <div className="sm:col-span-3">
              <Label
                id="is_use_private_flow"
                name={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.useForPrivateFlow"
                )}
              />
              <Controller
                control={control}
                name="is_use_private_flow"
                render={({ field: { onChange, value = true, ref } }) => (
                  <Input
                    type="checkbox"
                    label=""
                    id="is_use_private_flow"
                    checked={value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onChange(e.target.checked)
                    }
                  />
                )}
              />
              {get(errors, `is_use_private_flow`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `is_use_private_flow.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>
          ) : (
            <></>
          )}
          {canUseActiupPrivateFlow ? (
            <div className="sm:col-span-3">
              <Label id="is_use_actiup_private_flow" name="Use for ActiUp private flow" />
              <Controller
                control={control}
                name="is_use_actiup_private_flow"
                render={({ field: { onChange, value = true, ref } }) => (
                  <Input
                    type="checkbox"
                    label=""
                    id="is_use_actiup_private_flow"
                    checked={value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onChange(e.target.checked)
                    }
                  />
                )}
              />
              {get(errors, `is_use_actiup_private_flow`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `is_use_actiup_private_flow.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>
          ) : <></>}
          <div className="col-span-full">
            <Label
              id="total_for_sell"
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.totalForSell"
              )}
            />
            <Input
              id="total_for_sell"
              type="number"
              min={0}
              placeholder={translate(
                "eventSetup.phaseAndTicket.modal.ticket.totalForSell"
              )}
              {...register(`total_for_sell`, {
                valueAsNumber: true,
              })}
              error={get(errors, `total_for_sell.message`, "")}
            />
            {get(errors, `total_for_sell`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `total_for_sell.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
            <p className="text-sm">
              {translate("eventSetup.phaseAndTicket.modal.ticket.priceInfo")}
            </p>
          </div>
          <div className="col-span-full">
            <Controller
              control={control}
              name="price_type"
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <div className="flex flex-row flex-wrap gap-x-3">
                    {PRICE_TYPES.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        className={`rounded-md ${
                          type.value === value
                            ? "bg-[#A7B4FE] hover:bg-indigo-300 focus-visible:outline-indigo-300"
                            : "bg-white ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        } px-6 py-3 text-sm font-semibold text-brand-500 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2`}
                        onClick={() => {
                          onChange(type.value);
                          if (type.value === PriceType.FREE) {
                            setValue("price", 0, { shouldValidate: true });
                            setValue("original_price", 0, {
                              shouldValidate: true,
                            });
                          }
                        }}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                );
              }}
            />
            {get(errors, `price_type`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `price_type.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label
              id="original_price"
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.originalPrice"
              )}
              required
            />
            <div className="flex">
              <Input
                id="original_price"
                type="number"
                decimal={2}
                min={0}
                className="rounded-r-none"
                placeholder={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.originalPrice"
                )}
                disabled={watch("price_type") === PriceType.FREE}
                error={get(errors, "original_price")}
                {...register("original_price", {
                  valueAsNumber: true,
                })}
              />
              <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                {eventDetailData.currency}
              </span>
            </div>
            {get(errors, `original_price`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `original_price.message`, "")}
                </span>
              </p>
            ) : null}
          </div>

          <div className="sm:col-span-3">
            <Label
              id="price"
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.sellPrice"
              )}
              required
            />
            <div className="flex">
              <Input
                id="price"
                type="number"
                decimal={2}
                min={0}
                className="rounded-r-none"
                placeholder={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.sellPrice"
                )}
                disabled={watch("price_type") === PriceType.FREE}
                error={get(errors, "price")}
                {...register("price", {
                  valueAsNumber: true,
                })}
              />
              <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                {eventDetailData.currency}
              </span>
            </div>
            {get(errors, `price`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `price.message`, "")}
                </span>
              </p>
            ) : null}
          </div>

          <div className="sm:col-span-3">
            <Label
              id="sell_from"
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.startDate"
              )}
              required
            />
            <Controller
              control={control}
              name="sell_from"
              render={({ field: { onChange, value = "" } }) => {
                return (
                  <DateTimePicker
                    id="sell_from"
                    placeholder={translate(
                      "eventSetup.phaseAndTicket.modal.ticket.startDate"
                    )}
                    className="bg-white"
                    error={get(errors, `sell_from.message`, "")}
                    value={value}
                    onChange={(date: any) =>
                      date === null
                        ? onChange("")
                        : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))
                    }
                  />
                );
              }}
            />
            {get(errors, `sell_from`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `sell_from.message`, "")}
                </span>
              </p>
            ) : null}
          </div>

          <div className="sm:col-span-3">
            <Label
              id="sell_to"
              name={translate("eventSetup.phaseAndTicket.modal.ticket.endDate")}
              required
            />
            <Controller
              control={control}
              name="sell_to"
              render={({ field: { onChange, value = "" } }) => {
                return (
                  <DateTimePicker
                    id="sell_to"
                    placeholder={translate(
                      "eventSetup.phaseAndTicket.modal.ticket.endDate"
                    )}
                    className="bg-white"
                    error={get(errors, `sell_to.message`, "")}
                    value={value}
                    onChange={(date: any) =>
                      date === null
                        ? onChange("")
                        : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))
                    }
                  />
                );
              }}
            />
            {get(errors, `sell_to`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `sell_to.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
            <p className="text-sm">
              {translate(
                "eventSetup.phaseAndTicket.modal.ticket.participantFormInfo"
              )}
            </p>
          </div>
          <div className="col-span-full">
            <Controller
              control={control}
              name="is_package"
              render={({ field: { onChange, value, ref } }) => (
                <SwitchButton
                  label={translate(
                    "eventSetup.phaseAndTicket.modal.ticket.isPackage"
                  )}
                  checked={value ?? false}
                  onChange={(isChecked: boolean) => {
                    onChange(isChecked);
                    replace(DEFAULLT_TEMPLATE);
                    clearErrors("attendant_template");
                    if (isChecked) {
                      setValue("is_accept_upgrade_ticket", false, {
                        shouldValidate: true,
                      });
                      setValue("is_accept_transfer_ticket", false, {
                        shouldValidate: true,
                      });
                    }
                  }}
                />
              )}
            />
            {get(errors, `is_package`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `is_package.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="col-span-full">
            {watch("is_package") ? (
              <button
                type="button"
                className="flex flex-row items-center w-fit text-sm bg-white text-brand-500 active:bg-gray-50"
                onClick={() => {
                  append(DEFAULLT_TEMPLATE);
                  clearErrors("attendant_template");
                }}
              >
                <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
                <span className="shrink-0 text-sm">
                  {translate(
                    "eventSetup.phaseAndTicket.modal.ticket.addParticipantForms"
                  )}
                </span>
              </button>
            ) : (
              <></>
            )}
            <div className="divide-y divide-gray-300">
              {fields.map((field: IAttendantTemplateField, index: number) => (
                <div key={field.id} className="py-4">
                  {watch("is_package") && fields.length > 1 ? (
                    <div className="col-span-full flex justify-end">
                      <button
                        onClick={() => {
                          remove(index);
                          clearErrors("attendant_template");
                        }}
                      >
                        <TrashIcon className="w-6 h-6 text-brand-500" />
                      </button>
                    </div>
                  ) : (
                    <></>
                  )}
                  <div>
                    <Label
                      id={`attendant_template.${index}.template_id`}
                      name={translate(
                        "eventSetup.phaseAndTicket.modal.ticket.selectParticipantForm"
                      )}
                      required
                    />
                    <Controller
                      control={control}
                      name={`attendant_template.${index}.template_id`}
                      render={({ field: { onChange, value = "", ref } }) => {
                        return (
                          <Select
                            key={`attendant_template.${index}.template_id`}
                            inputRef={ref}
                            options={templatesDataArr}
                            id="template_id"
                            placeholder={translate(
                              "eventSetup.phaseAndTicket.modal.ticket.selectParticipantForm"
                            )}
                            defaultValue={
                              templatesDataArr.find(
                                (selected: { label: string; value: string }) =>
                                  selected.value === value
                              ) ?? ""
                            }
                            value={templatesDataArr.find(
                              (selected: { label: string; value: string }) =>
                                selected.value === value
                            )}
                            onChange={(selected: {
                              label: string;
                              value: string;
                            }) => {
                              onChange(selected?.value);
                            }}
                            error={get(
                              errors,
                              `attendant_template.${index}.template_id`,
                              ""
                            )}
                          />
                        );
                      }}
                    />
                    {get(
                      errors,
                      `attendant_template.${index}.template_id`,
                      ""
                    ) ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(
                            errors,
                            `attendant_template.${index}.template_id.message`,
                            ""
                          )}
                        </span>
                      </p>
                    ) : null}
                  </div>
                  {watch("is_package") ? (
                    <div className="mt-4">
                      <Label
                        id={`attendant_template.${index}.name`}
                        name={translate(
                          "eventSetup.phaseAndTicket.modal.ticket.formNameDisplayed"
                        )}
                      />
                      <div className="flex flex-col gap-y-4">
                        {eventDetailData.languages.map((lang) => (
                          <div key={lang}>
                            <div className="relative">
                              <Input
                                id={`attendant_template.${index}.name.${lang}`}
                                type="text"
                                className="pr-11"
                                placeholder={`${translate(
                                  "eventSetup.phaseAndTicket.modal.ticket.formNameDisplayed"
                                )} (${lang})`}
                                {...register(
                                  `attendant_template.${index}.name.${lang}`
                                )}
                                error={get(
                                  errors,
                                  `attendant_template.${index}.name.${lang}.message`,
                                  ""
                                )}
                              />
                              <img
                                src={`/images/flags/circle-${lang}.png`}
                                alt="Language Icon"
                                className="absolute top-1/2 right-3 -translate-y-1/2"
                              />
                            </div>
                            {get(
                              errors,
                              `attendant_template.${index}.name.${lang}`,
                              ""
                            ) ? (
                              <p className="error-text">
                                <span className="font-medium">
                                  {get(
                                    errors,
                                    `attendant_template.${index}.name.${lang}.message`,
                                    ""
                                  )}
                                </span>
                              </p>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              ))}
              {get(errors, `attendant_template`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `attendant_template.message`, "") ||
                      get(errors, `attendant_template.root.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>
          </div>
          <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
            <p className="text-sm">
              {translate(
                "eventSetup.phaseAndTicket.modal.ticket.advanceSettings"
              )}
            </p>
          </div>
          <div className="col-span-full flex flex-col items-start gap-y-4">
            <Controller
              control={control}
              name="hide_in_ui"
              render={({ field: { onChange, value, ref } }) => (
                <SwitchButton
                  label={translate(
                    "eventSetup.phaseAndTicket.modal.ticket.show"
                  )}
                  hint={translate(
                    "eventSetup.phaseAndTicket.modal.ticket.showHint"
                  )}
                  checked={!value}
                  onChange={(isChedked) => onChange(!isChedked)}
                />
              )}
            />
            <Controller
              control={control}
              name="is_accept_edit_participant_info"
              render={({ field: { onChange, value, ref } }) => (
                <SwitchButton
                  label={translate(
                    "eventSetup.phaseAndTicket.modal.ticket.acceptEditParticipantInfo"
                  )}
                  hint={translate(
                    "eventSetup.phaseAndTicket.modal.ticket.acceptEditParticipantInfoHint"
                  )}
                  checked={value ?? false}
                  onChange={onChange}
                />
              )}
            />
            <Controller
              control={control}
              name="is_accept_upgrade_ticket"
              render={({ field: { onChange, value, ref } }) => (
                <SwitchButton
                  label={translate(
                    "eventSetup.phaseAndTicket.modal.ticket.acceptUpgradeTicket"
                  )}
                  hint={translate(
                    "eventSetup.phaseAndTicket.modal.ticket.acceptUpgradeTicketHint"
                  )}
                  disabled={watch("is_package")}
                  checked={value ?? false}
                  onChange={onChange}
                />
              )}
            />
            <Controller
              control={control}
              name="is_accept_transfer_ticket"
              render={({ field: { onChange, value, ref } }) => (
                <SwitchButton
                  label={translate(
                    "eventSetup.phaseAndTicket.modal.ticket.acceptTransferTicket"
                  )}
                  hint={translate(
                    "eventSetup.phaseAndTicket.modal.ticket.acceptTransferTicketHint"
                  )}
                  disabled={watch("is_package")}
                  checked={value ?? false}
                  onChange={onChange}
                />
              )}
            />
            <Controller
              control={control}
              name="is_soldout"
              render={({ field: { onChange, value, ref } }) => (
                <SwitchButton
                  label={translate(
                    "eventSetup.phaseAndTicket.modal.ticket.isSoldOut"
                  )}
                  hint={translate(
                    "eventSetup.phaseAndTicket.modal.ticket.isSoldOutHint"
                  )}
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
          </div>
        </div>
      </div>
    </RightSideModal>
  );
};
